import React, { useEffect, useRef, useState } from 'react';
import { NaverMap as Map, Marker, useNavermaps } from 'react-naver-maps';
import StudentList from './StudentList';
import StudentForm from './StudentForm';
import AcademyForm from './AcademyForm';
import { supabase } from '../lib/supabase';
import { Student, Academy } from '../types';
import { UserPlus, Building2, Search, Filter } from 'lucide-react';
import { DndContext, DragOverlay, closestCenter, DragEndEvent, useSensors, useSensor, MouseSensor, TouchSensor, PointerSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';

declare global {
  interface Window {
    naver: any;
  }
}

const NaverMap: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showAcademyForm, setShowAcademyForm] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number; lng: number; marker?: any; type?: 'pickup' | 'dropoff'} | null>(null);
  const [selectedAcademy, setSelectedAcademy] = useState<Academy | null>(null);
  const academyMarkersRef = useRef<any[]>([]);
  const routePolylineRef = useRef<any>(null);
  const [routeInfo, setRouteInfo] = useState<{distance: number; duration: number} | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(PointerSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    // 여기에 드래그 앤 드롭 처리 로직 추가
    console.log('Drag ended:', { active, over });
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${import.meta.env.VITE_NAVER_CLIENT_ID}&submodules=geocoder,drawing,visualization`;
    script.async = true;
    
    script.onload = () => {
      if (!mapRef.current || !window.naver) return;

      // Direction 서비스 초기화
      const initDirectionService = () => {
        const directionScript = document.createElement('script');
        directionScript.src = 'https://openapi.map.naver.com/openapi/v3/maps.js?submodules=route';
        directionScript.async = true;
        
        directionScript.onload = () => {
          try {
            const mapOptions = {
              center: new window.naver.maps.LatLng(37.5665, 126.9780),
              zoom: 12,
              zoomControl: true,
              zoomControlOptions: {
                position: window.naver.maps.Position.TOP_RIGHT,
                style: window.naver.maps.ZoomControlStyle.SMALL
              }
            };

            mapInstance.current = new window.naver.maps.Map(mapRef.current, mapOptions);
            setMapLoaded(true);
          } catch (err) {
            setError('지도를 초기화하는 중 오류가 발생했습니다.');
            console.error('Map initialization error:', err);
          }
        };

        document.head.appendChild(directionScript);
      };

      initDirectionService();
    };
    
    script.onerror = () => {
      setError('지도 스크립트를 로드하는 중 오류가 발생했습니다.');
    };
    
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (mapLoaded) {
      loadStudents();
      loadAcademies();
    }
  }, [mapLoaded]);

  const loadStudents = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading students:', error);
      return;
    }

    setStudents(data);
  };

  const loadAcademies = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('academies')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading academies:', error);
      return;
    }

    setAcademies(data);
    updateAcademyMarkers(data);
  };

  const updateAcademyMarkers = (academyData: Academy[]) => {
    if (!window.naver || !mapInstance.current) return;

    // Clear existing markers
    academyMarkersRef.current.forEach(marker => marker.setMap(null));
    academyMarkersRef.current = [];

    academyData.forEach(academy => {
      window.naver.maps.Service.geocode({
        query: academy.address
      }, (status: any, response: any) => {
        if (status === window.naver.maps.Service.Status.ERROR) return;

        if (response.v2.meta.totalCount > 0) {
          const item = response.v2.addresses[0];
          const point = new window.naver.maps.LatLng(item.y, item.x);
          
          const marker = new window.naver.maps.Marker({
            position: point,
            map: mapInstance.current,
            icon: {
              content: `
                <div style="position: relative;">
                  <div style="position: absolute; top: -40px; left: 50%; transform: translateX(-50%); white-space: nowrap; background: #4A90E2; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px;">
                    ${academy.name}
                  </div>
                  <div style="font-size: 24px;">🚩</div>
                </div>
              `,
              anchor: new window.naver.maps.Point(12, 24)
            }
          });

          academyMarkersRef.current.push(marker);
        }
      });
    });
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}시간 ${minutes}분`;
    }
    return `${minutes}분`;
  };

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)}km`;
    }
    return `${meters}m`;
  };

  const handleAddressClick = async (address: string, studentName: string, type: 'pickup' | 'dropoff') => {
    if (!window.naver || !mapInstance.current) return;

    window.naver.maps.Service.geocode({
      query: address
    }, async (status: any, response: any) => {
      if (status === window.naver.maps.Service.Status.ERROR) {
        alert('주소를 찾을 수 없습니다.');
        return;
      }

      if (response.v2.meta.totalCount > 0) {
        const item = response.v2.addresses[0];
        const point = new window.naver.maps.LatLng(item.y, item.x);
        
        if (selectedLocation?.marker) {
          selectedLocation.marker.setMap(null);
        }

        if (routePolylineRef.current) {
          routePolylineRef.current.setMap(null);
          routePolylineRef.current = null;
        }

        setRouteInfo(null);

        const marker = new window.naver.maps.Marker({
          position: point,
          map: mapInstance.current,
          icon: {
            content: `
              <div style="position: relative;">
                <div style="position: absolute; top: -32px; left: 50%; transform: translateX(-50%); white-space: nowrap; background: #4A90E2; color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px;">
                  ${studentName} (${type === 'pickup' ? '등원' : '하원'})
                </div>
                <img src="https://navermaps.github.io/maps.js.ncp/docs/img/example/pin_default.png" />
              </div>
            `,
            anchor: new window.naver.maps.Point(12, 24)
          }
        });

        setSelectedLocation({ lat: parseFloat(item.y), lng: parseFloat(item.x), marker, type });

        if (selectedAcademy) {
          try {
            const academyGeocode = await new Promise((resolve, reject) => {
              window.naver.maps.Service.geocode({
                query: selectedAcademy.address
              }, (status: any, response: any) => {
                if (status === window.naver.maps.Service.Status.ERROR) {
                  reject(new Error('학원 주소를 찾을 수 없습니다.'));
                  return;
                }
                if (response.v2.meta.totalCount > 0) {
                  resolve(response.v2.addresses[0]);
                } else {
                  reject(new Error('학원 주소를 찾을 수 없습니다.'));
                }
              });
            });

            if (!window.naver.maps.Service.Direction) {
              throw new Error('Direction 서비스가 초기화되지 않았습니다.');
            }

            const directionResponse = await new Promise((resolve, reject) => {
              window.naver.maps.Service.Direction.fromCoordToCoord(
                new window.naver.maps.LatLng(item.y, item.x),
                new window.naver.maps.LatLng(academyGeocode.y, academyGeocode.x),
                { 
                  waypoints: [],
                  option: 'traoptimal' 
                },
                (status: any, response: any) => {
                  if (status === window.naver.maps.Service.Status.ERROR) {
                    reject(new Error('경로를 찾을 수 없습니다.'));
                    return;
                  }
                  resolve(response);
                }
              );
            });

            const route = directionResponse.route.traoptimal[0];
            const path = route.path.map((coord: number[]) => 
              new window.naver.maps.LatLng(coord[1], coord[0])
            );

            if (routePolylineRef.current) {
              routePolylineRef.current.setMap(null);
            }

            routePolylineRef.current = new window.naver.maps.Polyline({
              path: path,
              strokeColor: type === 'pickup' ? '#2ecc71' : '#e74c3c',
              strokeWeight: 5,
              strokeOpacity: 0.8,
              map: mapInstance.current
            });

            // 현실적인 소요 시간 계산 (40km/h 평균 속도 가정)
            const distanceKm = route.summary.distance / 1000;
            const estimatedTimeHours = distanceKm / 40;
            const estimatedTimeSeconds = Math.round(estimatedTimeHours * 3600);

            setRouteInfo({
              distance: route.summary.distance,
              duration: estimatedTimeSeconds
            });

            const bounds = path.reduce((bounds, coord) => {
              return bounds.extend(coord);
            }, new window.naver.maps.LatLngBounds());
            
            mapInstance.current.fitBounds(bounds, {
              top: 100,
              right: 100,
              bottom: 100,
              left: 100
            });
          } catch (error) {
            console.error('Error getting directions:', error);
            alert('경로를 가져오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
          }
        } else {
          mapInstance.current.setCenter(point);
          mapInstance.current.setZoom(15);
        }
      }
    });
  };

  const handleSaveStudent = (student: Student) => {
    if (editingStudent) {
      setStudents(prev => prev.map(s => s.id === student.id ? student : s));
      setEditingStudent(null);
    } else {
      setStudents(prev => [...prev, student]);
    }
  };

  const handleSaveAcademy = (academy: Academy) => {
    setAcademies(prev => [...prev, academy]);
    updateAcademyMarkers([...academies, academy]);
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = searchTerm === '' || 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.pickup_address1.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.dropoff_address1.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSchool = selectedSchools.length === 0 || 
      selectedSchools.includes(student.school);

    return matchesSearch && matchesSchool;
  });

  const uniqueSchools = Array.from(new Set(students.map(s => s.school))).sort();

  if (error) {
    return (
      <div className="rounded-lg overflow-hidden shadow-lg bg-white p-4">
        <div className="text-red-600">
          <p>{error}</p>
          <p className="text-sm mt-2">
            다음을 확인해주세요:
            <ul className="list-disc ml-5 mt-1">
              <li>네이버 클라우드 플랫폼에서 현재 도메인이 등록되어 있는지 확인</li>
              <li>환경 변수 (VITE_NAVER_CLIENT_ID)가 올바르게 설정되어 있는지 확인</li>
            </ul>
          </p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-6">
        <div className="flex-1">
          <div className="rounded-lg overflow-hidden shadow-lg">
            <div 
              ref={mapRef}
              style={{ width: '100%', height: '600px' }}
              className="w-full h-[600px]"
            >
              {/* 지도는 useEffect에서 초기화됨 */}
            </div>
            {routeInfo && (
              <div className="bg-white p-4 border-t">
                <p className="text-gray-700">
                  <span className="font-semibold">예상 소요 시간:</span> {formatDuration(routeInfo.duration)}
                  <span className="mx-2">|</span>
                  <span className="font-semibold">이동 거리:</span> {formatDistance(routeInfo.distance)}
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="w-80">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowStudentForm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UserPlus size={20} />
              학생 입력
            </button>
            <button
              onClick={() => setShowAcademyForm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Building2 size={20} />
              학원 입력
            </button>
          </div>

          {academies.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                경로 표시할 학원 선택
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={selectedAcademy?.id || ''}
                onChange={(e) => {
                  const academy = academies.find(a => a.id === e.target.value);
                  setSelectedAcademy(academy || null);
                }}
              >
                <option value="">선택 안함</option>
                {academies.map(academy => (
                  <option key={academy.id} value={academy.id}>
                    {academy.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-4 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="학생 이름 또는 주소 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <Filter size={20} className="text-gray-500 flex-shrink-0" />
              {uniqueSchools.map(school => (
                <button
                  key={school}
                  onClick={() => {
                    setSelectedSchools(prev =>
                      prev.includes(school)
                        ? prev.filter(s => s !== school)
                        : [...prev, school]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                    selectedSchools.includes(school)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {school}
                </button>
              ))}
            </div>
          </div>
          
          <StudentList
            students={filteredStudents}
            onAddressClick={handleAddressClick}
            onEdit={(student) => {
              setEditingStudent(student);
              setShowStudentForm(true);
            }}
          />
        </div>

        {showStudentForm && (
          <StudentForm
            onClose={() => setShowStudentForm(false)}
            onSave={handleSaveStudent}
            student={editingStudent}
          />
        )}

        {showAcademyForm && (
          <AcademyForm
            onClose={() => setShowAcademyForm(false)}
            onSave={handleSaveAcademy}
          />
        )}
      </div>
    </DndContext>
  );
};

export default NaverMap;